import { Button, Select, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import tw from 'twin.macro';

import { useAllBranches } from '@/hooks/queries/admin';

interface Props {
  value?: number[];
  onChange?: (value: number[]) => void;
  disabled?: boolean;
}

/**
 * 지점을 검색 · 선택하면 아래에 태그로 쌓인다. 태그의 x 로 개별 해제.
 * Form.Item 의 value / onChange 를 그대로 받는다.
 */
const BranchTagSelect = ({ value = [], onChange, disabled = false }: Props) => {
  const { data: branches = [], isFetching } = useAllBranches();

  const branchNameById = useMemo(
    () => new Map(branches.map(({ id, branchName }) => [id, branchName])),
    [branches]
  );
  const options = useMemo(() => {
    const selected = new Set(value);

    return branches
      .filter(({ id }) => !selected.has(id))
      .map(({ id, branchName }) => ({ label: branchName, value: id }));
  }, [branches, value]);

  return (
    <div css={tw`flex flex-col gap-[8px] w-full`}>
      <div css={tw`w-full [.ant-select]:w-full`}>
        <Select<number | null>
          showSearch
          allowClear={false}
          value={null}
          placeholder="지점명을 검색해서 선택하세요"
          optionFilterProp="label"
          loading={isFetching}
          disabled={disabled}
          options={options}
          notFoundContent="선택할 수 있는 지점이 없어요"
          onSelect={(branchId) => {
            if (branchId !== null) {
              onChange?.([...value, branchId]);
            }
          }}
        />
      </div>

      <div
        css={tw`flex flex-row flex-wrap gap-[8px] p-[8px] min-h-[40px] max-h-[160px] overflow-y-auto border border-solid border-[#f0f0f0] rounded-[6px]`}
      >
        {value.length === 0 ? (
          <Typography.Text type="secondary">
            선택된 지점이 없어요
          </Typography.Text>
        ) : (
          value.map((branchId) => (
            <Tag
              key={branchId}
              css={tw`m-0`}
              color={disabled ? 'default' : 'blue'}
              closable={!disabled}
              onClose={(e) => {
                e.preventDefault();
                onChange?.(value.filter((id) => id !== branchId));
              }}
            >
              {branchNameById.get(branchId) ?? `지점 #${branchId}`}
            </Tag>
          ))
        )}
      </div>

      {value.length > 0 && (
        <div css={tw`flex flex-row items-center justify-between`}>
          <Typography.Text type="secondary">
            {value.length}개 선택
          </Typography.Text>
          <Button
            type="link"
            size="small"
            disabled={disabled}
            onClick={() => onChange?.([])}
          >
            전체 해제
          </Button>
        </div>
      )}
    </div>
  );
};

export default BranchTagSelect;
